import { useEmployeeSearchColumns } from "@/queries/employees/use-employees.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import {
  ApiEmployeeColumn,
  EmployeeColumnEntity,
  getEmployeeColumnEntity,
  parseCustomTableKey,
} from "@/types/employees/employee-column.types.ts";
import { groupBy } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Порядок групп в модалке настройки колонок и фильтров
export const ENTITY_ORDER: EmployeeColumnEntity[] = [
  EmployeeColumnEntity.personal,
  EmployeeColumnEntity.position,
  EmployeeColumnEntity.employment,
  EmployeeColumnEntity.compensation,
  EmployeeColumnEntity.termination,
  EmployeeColumnEntity.additional_compensation,
  EmployeeColumnEntity.custom_field,
  EmployeeColumnEntity.custom_table,
];

export const useEmployeeFilterCategories = () => {
  const { t } = useTranslation();
  const { data: catalog, isLoading, isError } = useEmployeeSearchColumns();
  const { data: appConfig } = useAppConfig();

  const catalogColumns = useMemo<ApiEmployeeColumn[]>(() => catalog ?? [], [catalog]);

  const tableTitleById = useMemo(() => {
    const result = new Map<number, string>();
    appConfig?.ui.employee_profile.tabs.forEach(({ items }) =>
      items.forEach(({ table }) => {
        if (table) result.set(table.id, table.title);
      })
    );
    return result;
  }, [appConfig]);

  const columnByKey = useMemo(
    () => new Map(catalogColumns.map((column) => [column.id, column])),
    [catalogColumns]
  );

  const columnsByEntity = useMemo(
    () => groupBy(catalogColumns, (column) => getEmployeeColumnEntity(column.id)),
    [catalogColumns]
  );

  const entityTitles = useMemo<Record<EmployeeColumnEntity, string>>(
    () => ({
      [EmployeeColumnEntity.personal]: t("core:personal"),
      [EmployeeColumnEntity.position]: t("core:position"),
      [EmployeeColumnEntity.employment]: t("core:employment"),
      [EmployeeColumnEntity.compensation]: t("core:compensation"),
      [EmployeeColumnEntity.termination]: t("core:termination"),
      [EmployeeColumnEntity.additional_compensation]: t("core:additional_compensation"),
      [EmployeeColumnEntity.custom_field]: t("custom_fields:custom_fields"),
      [EmployeeColumnEntity.custom_table]: t("custom_fields:custom_tables"),
    }),
    [t]
  );

  const filterCategories = useMemo(() => {
    return ENTITY_ORDER.map((entity) => ({
      id: entity,
      title: entityTitles[entity],
      columns: (columnsByEntity[entity] ?? [])
        .filter((column) => column.is_filterable)
        .map((column) => {
          const customTable = parseCustomTableKey(column.id);
          if (!customTable) return column;
          return {
            ...column,
            groupId: String(customTable.tableId),
            groupTitle: tableTitleById.get(customTable.tableId),
          };
        }),
    })).filter((category) => category.columns.length > 0);
  }, [columnsByEntity, entityTitles, tableTitleById]);

  return {
    catalogColumns,
    columnByKey,
    columnsByEntity,
    entityTitles,
    tableTitleById,
    filterCategories,
    isLoading,
    isError,
  };
};
